import { api, serializeBody } from '@connected/api-client';
import { me } from '@connected/api-client/data/auth/me';
import { validators } from '@connected/api-client/generated/validators.generated';
import { setAuthentication } from '@connected/api-client/lib/authentication';
import { isProjectPath } from '@connected/api-client/lib/login';
import { DOMAIN_KEYS } from '@connected/api-client/lib/portal';
import { navigateWithSSO } from '@connected/api-client/lib/sso';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { ContentLayout } from '@connected/ui/layouts/content/layout';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { queryClient } from '@/lib/query-client';

type FormValues = {
    email: string;
    password: string;
};

export const Route = createFileRoute('/_unauth/login/')({
    component: Page,
    validateSearch: z.object({
        redirectHost: z.string().optional(),
        redirectPath: z.string().optional(),
        from: z.enum(DOMAIN_KEYS).optional(),
    }),
});

const DEFAULT_VALUES: FormValues = {
    email: '',
    password: '',
};

function Page() {
    const { t } = useTranslation('unauth');
    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const mutation = api.useMutation('post', '/api/v2/oauth2/token', {
        onSuccess: async (data) => {
            setAuthentication({
                accessToken: data.access_token,
                expiresIn: data.expires_in,
                tokenType: data.token_type,
                issuedAt: data['.issued'],
            });

            await queryClient.invalidateQueries(me.queryOptions());

            const meData = await queryClient.ensureQueryData(me.queryOptions());

            // If from elektra-app AND has project path, redirect back via SSO
            if (search.from === 'elektra-app' && search.redirectPath && isProjectPath(search.redirectPath)) {
                navigateWithSSO({
                    targetDomain: 'elektra-app',
                    redirectPath: search.redirectPath,
                });
                return;
            }

            // If from elektra-app but no project path, stay in portaal dashboard
            if (search.from === 'elektra-app') {
                if (meData?.IsAdmin) {
                    await navigate({
                        to: '/dashboard/organizations',
                        replace: true,
                    });
                    return;
                }
                if (meData?.Organization?.OrganizationId) {
                    await navigate({
                        to: '/dashboard/organizations/$organizationId/projects',
                        params: {
                            organizationId: meData.Organization.OrganizationId,
                        },
                        replace: true,
                    });
                    return;
                }
            }

            // Default: internal portaal navigation
            await navigate({
                to: search.redirectPath || '/',
            });
        },
        onError: error => toast.error(t(`login.error.${(error as { error: string })?.error}`)),
    });

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: ({ value }) => mutation.mutate({
            body: {
                grant_type: 'password',
                username: value.email,
                password: value.password,
            },
            bodySerializer: serializeBody,
        }),
    });

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <ContentLayout className="h-full w-full items-center justify-center gap-3 max-w-none">
            <ContentLayout.Header className="w-full max-w-sm">
                <ContentLayout.Title className="text-center text-3xl pb-1">
                    {t(`login.title`)}
                </ContentLayout.Title>
                <ContentLayout.SubTitle className="text-center text-base font-normal px-3 pb-2">
                    {
                        search.from
                            ? (
                                    <Trans
                                        i18nKey="login.subtitle-from"
                                        ns="unauth"
                                        components={{
                                            domainColor: (
                                                <span style={{
                                                    color: getDomainColor(search.from || ''),
                                                    fontWeight: 'bold',
                                                }}
                                                />
                                            ),
                                        }}
                                        values={{
                                            domain: search.from || 'Connected',
                                        }}
                                    />
                                )
                            : t('login.subtitle')
                    }
                </ContentLayout.SubTitle>
            </ContentLayout.Header>
            <form className="w-full max-w-sm flex flex-col gap-4 p-3 pt-0" onSubmit={handleSubmit}>
                <form.AppForm>
                    <form.AppField
                        name="email"
                        validators={{
                            onSubmit:
                                validators.oAuth2TokenV2Request.username,
                        }}
                    >
                        {field => (
                            <field.Label>
                                <field.Label.Title label={t('login.fields.email.title')} />
                                <field.Input
                                    type="email"
                                    placeholder={t('login.fields.email.placeholder')}
                                />
                                <field.ValidationError />
                            </field.Label>
                        )}
                    </form.AppField>
                    <form.AppField
                        name="password"
                        validators={{
                            onSubmit: z
                                .string()
                                .min(1, t('login.fields.password.error-min-characters')),
                        }}
                    >
                        {field => (
                            <field.Label>
                                <field.Label.Title label={t('login.fields.password.title')} />
                                <field.Input
                                    type="password"
                                    placeholder={t(
                                        'login.fields.password.placeholder',
                                    )}
                                />
                                <field.ValidationError />
                            </field.Label>
                        )}
                    </form.AppField>

                    <form.SubmitButton asChild>
                        <Button
                            type="submit"
                            className="mt-2 text-base"
                            disabled={mutation.isPending}
                        >
                            {t('login.do-login')}
                        </Button>
                    </form.SubmitButton>
                </form.AppForm>
                {/* Register account button in place for design, not functional */}
                {/* <span className="flex items-center justify-center text-sm mt-1">
                        {t('login.no-account-yet')}
                        <Button
                            variant="link"
                            asChild
                            className="font-semibold ps-1"
                        >
                            <Link to="/">{t('login.register-account')}</Link>
                        </Button>
                    </span> */}
                <Button
                    variant="link"
                    className="font-semibold pt-0"
                    asChild
                >
                    <Link to="/forgot-password">
                        {t('login.forgot-password')}
                    </Link>
                </Button>
            </form>
        </ContentLayout>
    );
}

function getDomainColor(domain: string) {
    if (!domain)
        return '#000000';

    const domainLower = domain.toLowerCase();
    switch (true) {
        case domainLower.includes('elektra-app'):
            return '#df4f16'; // Elektra color
        default:
            return '#000000'; // Default color if no match
    }
}
